import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import isDifferent from 'deep-diff';
import { Icon } from '@panel/commons';
import { useTranslation } from 'react-i18next';
import { useQuery, usePreventWindowUnload, useModal } from '@panel/commons/hooks';
import { Step1Module1Selectors, Step1Module1Operations } from '../../../ducks/step1Module1';
import { SavePostingOperations } from '../../../ducks/savePosting';
import { WrapperContainer } from '../../Wrappers/WrapperContainer';
import { Step1Module1Schema } from './validations';
import { isNotEmptyNull } from '../../../utils/collections';
import { module1BodyManager } from '../../../utils/bodyManager';
import { sendEvent } from '../../../utils/trackingEvent';
import { DisabledBranchError } from '../../Modals/disabledBranchError';
import { Modal } from '../../Modals/ModalWrapper';
import { useStepperCtx } from '../../stepper';
import { updateLastStep } from '../../../ducks/stepper';
import {
  SubWrapper,
  ModuleTitle,
  OperationTypeWrapper,
  OperationTypeChoice,
  OperationTypeRadio,
  PropertyTypeWrapper,
  SelectWrapper,
  SelectLabel,
  Select,
  IconWrapper,
  Option,
  ErrorMessage
} from './styles';
import { SeekerGuard } from '../../seeker/seekerGuard';

const operationTypeFilterOptions = ['1', '2', '4'];

const Module1 = WrapperContainer(props => {
  const { setId, loading } = props;
  const {
    propertyTypes,
    propertySubTypes,
    formValues,
    hasValues,
    isCompleted,
    disabledBranch,
    originalValues
  } = useSelector(Step1Module1Selectors.getStep1Module1State);
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const { setPreventTransition, nextStepperRoute } = useStepperCtx();
  const { isShowing, toggleValue } = useModal();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const defaultValues = originalValues;
  const isDirty = isDifferent(originalValues, formValues);
  const { selectedOperationTypeId, selectedPropertyTypeId, selectedPropertySubTypeId } = formValues;
  const { register, handleSubmit, errors, setValue } = useForm({
    defaultValues,
    mode: 'onBlur',
    shouldFocusError: true,
    resolver: yupResolver(Step1Module1Schema),
    context: {
      validateSubType: isNotEmptyNull(propertySubTypes)
    }
  });

  useEffect(() => {
    setId('formModule1');
  }, [setId]);

  useEffect(() => {
    if (!propertyTypes) {
      dispatch(Step1Module1Operations.getPropertyTypes());
    }
  }, [dispatch, propertyTypes]);

  useEffect(() => {
    if (disabledBranch) {
      toggleValue(disabledBranch);
    }
  }, [disabledBranch, toggleValue]);

  useEffect(() => {
    if (selectedPropertyTypeId) {
      dispatch(Step1Module1Operations.getPropertySubTypes(selectedPropertyTypeId));
    }
  }, [dispatch, selectedPropertyTypeId]);

  useEffect(() => {
    if (isCompleted && !hasValues) {
      dispatch(Step1Module1Operations.setEditValues(formValues));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, hasValues, isCompleted]);

  useEffect(() => {
    if (postingId && !hasValues) {
      const payload = {
        module: 'OPERATION',
        step: 'STEP_MAIN',
        postingId
      };
      dispatch(Step1Module1Operations.getFormValues(payload));
    }
  }, [dispatch, hasValues, postingId]);

  useEffect(() => {
    return () => {
      dispatch(Step1Module1Operations.cleanValues());
      if (location.search.includes('?tasador')) {
        dispatch(Step1Module1Operations.setRedirectFromAppraisal());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDirty) {
      setPreventTransition(formRef);
    } else {
      setPreventTransition(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  const onSubmit = (data, e) => {
    const { nativeEvent } = e;

    const path = nextStepperRoute || '/publicador/principales/ubicacion';
    const payload = {
      body: module1BodyManager({ ...data, postingId }),
      step: 'STEP_MAIN',
      module: 'OPERATION'
    };
    const saveAndExit = nativeEvent.submitter && nativeEvent.submitter.id === 'saveAndExit';
    const savedFromModal = nativeEvent.detail && nativeEvent.detail.submitter === 'saveAndRedirect';

    const propertyObj = { list: propertyTypes, id: selectedPropertyTypeId };
    const subPropertyObj = { list: propertySubTypes, id: selectedPropertySubTypeId };

    sendEvent(
      'Principales Modulo1',
      saveAndExit ? 'Guardar y salir' : 'Continuar',
      selectedOperationTypeId,
      propertyObj,
      subPropertyObj
    );

    dispatch(SavePostingOperations.savePostingDB(payload, path, saveAndExit, savedFromModal));

    dispatch(updateLastStep('1', '1'));
  };

  const onClose = () => {
    toggleValue(false);
    dispatch(Step1Module1Operations.setDisabledBranch(false));
    window.location.href = '/panel/avisos';
  };

  const onSelectOperationType = operationTypeId => {
    dispatch(Step1Module1Operations.setOperationType(operationTypeId));
  };
  const onSelectPropertyType = e => {
    const { value } = e.target;
    dispatch(Step1Module1Operations.setPropertyType(value));
  };
  const onSelectPropertySubType = e => {
    const { value } = e.target;
    setValue('propertySubType', value);
    dispatch(Step1Module1Operations.setPropertySubType(value));
  };

  usePreventWindowUnload(isDirty);

  return (
    <SubWrapper>
      <SeekerGuard />
      <ModuleTitle>{t('publisher.step1.module1.title')}</ModuleTitle>
      <form id='formModule1' data-repa='formModule1' ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={loading} data-repa='formModule1-operation-fieldset'>
          <OperationTypeWrapper>
            <SelectLabel htmlFor={`operationTypeId${selectedOperationTypeId}`}>
              {t('publisher.step1.module1.operationType.title')}
            </SelectLabel>
            <div>
              {operationTypeFilterOptions.sort().map(operationTypeId => (
                <OperationTypeChoice
                  onClick={() => onSelectOperationType(operationTypeId)}
                  checked={selectedOperationTypeId === operationTypeId}
                  key={`operationTypeId${operationTypeId}`}
                  data-repa={`formModule1-operation-id${operationTypeId}`}
                  disabled={(postingId && !propertyTypes) || loading}
                >
                  <OperationTypeRadio
                    data-repa={`formModule1-operation-typeId${operationTypeId}`}
                    name='operationType'
                    id={`operationTypeId${operationTypeId}`}
                    type='radio'
                    value={operationTypeId}
                    checked={selectedOperationTypeId === operationTypeId}
                    ref={register}
                    disabled={postingId && !propertyTypes}
                    onChange={() => onSelectOperationType(operationTypeId)}
                  />
                  <label
                    htmlFor={`operationTypeId${operationTypeId}`}
                    data-repa={`formModule1-operation-labelfor-id${operationTypeId}`}
                  >
                    {t(`publisher.step1.module1.operationType.${operationTypeId}`)}
                  </label>
                </OperationTypeChoice>
              ))}
            </div>
          </OperationTypeWrapper>
          <ErrorMessage>
            {errors.operationType && `${t('publisher.step1.module1.operationType.validation')}`}
          </ErrorMessage>
          <PropertyTypeWrapper>
            <SelectWrapper>
              <SelectLabel htmlFor='propertyType' data-repa='formModule1-propertyType-selectLabel'>
                {t('publisher.step1.module1.propertyType.title')}
              </SelectLabel>
              <Select
                id='propertyType'
                name='propertyType'
                ref={register}
                value={selectedPropertyTypeId}
                onInput={onSelectPropertyType}
                data-repa='formModule1-propertyType-select'
                onChange={() => {}}
              >
                <Option value='' data-repa='formModule1-propertyType-select-option'>
                  {t('publisher.step1.module1.propertyType.placeholder')}
                </Option>
                {propertyTypes &&
                  propertyTypes.map(propertyType => (
                    <Option
                      key={`propertyType-${propertyType.realEstateTypeId}`}
                      data-repa={`formModule1-propertyType-select-option-${propertyType.realEstateTypeId}`}
                      value={propertyType.realEstateTypeId}
                    >
                      {propertyType.name}
                    </Option>
                  ))}
              </Select>
              <IconWrapper>
                <Icon name='chevronDown' width={16} height={16} viewBox='0 0 16 16' />
              </IconWrapper>
              <ErrorMessage>
                {errors.propertyType && `${t('publisher.step1.module1.propertyType.validation')}`}
              </ErrorMessage>
            </SelectWrapper>
            <SelectWrapper>
              <SelectLabel
                disabled={!(propertySubTypes && propertySubTypes.length > 0)}
                data-repa='formModule1-subPropertyType-selectLabel'
                htmlFor='propertySubType'
              >
                {t('publisher.step1.module1.propertySubType.title')}
              </SelectLabel>
              <Select
                disabled={!(propertySubTypes && propertySubTypes.length > 0 && selectedPropertyTypeId)}
                id='propertySubType'
                name='propertySubType'
                ref={register}
                value={selectedPropertySubTypeId}
                onInput={onSelectPropertySubType}
                data-repa='formModule1-subPropertyType-select'
              >
                <Option value=''>{t('publisher.step1.module1.propertySubType.placeholder')}</Option>
                {propertySubTypes &&
                  propertySubTypes.map(propertySubType => (
                    <Option
                      key={propertySubType.realEstateSubTypeId}
                      data-repa={`formModule1-subPropertyType-select-option-${propertySubType.realEstateSubTypeId}`}
                      value={propertySubType.realEstateSubTypeId}
                    >
                      {propertySubType.name}
                    </Option>
                  ))}
              </Select>
              <IconWrapper>
                <Icon name='chevronDown' width={16} height={16} viewBox='0 0 16 16' />
              </IconWrapper>
              <ErrorMessage>
                {errors.propertySubType &&
                  selectedPropertyTypeId &&
                  `${t('publisher.step1.module1.propertySubType.validation')}`}
              </ErrorMessage>
            </SelectWrapper>
          </PropertyTypeWrapper>
        </fieldset>
      </form>
      {isShowing && (
        <Modal>
          <DisabledBranchError openModal={isShowing} onClose={onClose} handleClick={onClose} />
        </Modal>
      )}
    </SubWrapper>
  );
});

export { Module1 };
